import React, {useState, useEffect, Fragment} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import Loading from './loading';
import Show from './show';

const category = [
  {
    label: '新制',
    value: 1,
  },
  {
    label: '舊制',
    value: 0,
  },
];

const nationality = [
  {
    label: '本國人',
    value: 0,
  },
  {
    label: '外籍',
    value: 1,
  },
  {
    label: '外籍配偶',
    value: 2,
  },
  {
    label: '大陸配偶',
    value: 3,
  },
];

const LaborPensionData = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    onBoardingDate,
    getInsuranceAmount,
    updateLocale,
    defaultEmployeeId,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, errors, setValue, trigger} = formProps;
  const isUnable = !useWatch({
    control,
    name: `retirement.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isNewCategory =
    useWatch({
      control,
      name: `retirement.category`,
      defaultValue: defaultValues.category,
    }) === 1;
  const effectiveDate = useWatch({
    control,
    name: `retirement.effectiveDate`,
    defaultValue: defaultValues.effectiveDate,
  });
  const employeeId = useWatch({
    control,
    name: `employee.id`,
    defaultValue: defaultEmployeeId,
  });
  const [insuranceAmount, setInsuranceAmount] = useState([]);
  const [isInsuranceAmountFetching, setIsInsuranceAmountFetching] =
    useState(false);
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);
  useEffect(() => {
    if (defaultValues.effectiveDate && employeeId) {
      setIsInsuranceAmountFetching(true);
      getInsuranceAmount({
        amountLevelType: 1,
        insuranceAmountLevelId: undefined,
        effectiveDate: defaultValues.effectiveDate,
        insuranceIdentityId: undefined,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  }, [defaultValues.effectiveDate, employeeId]);

  const updateInsuranceAmount = ({
    effectiveDateInput,
  }: {
    effectiveDateInput?: string;
  }) => {
    if (effectiveDateInput || effectiveDate) {
      setIsInsuranceAmountFetching(true);
      setInsuranceAmount([]);
      getInsuranceAmount({
        amountLevelType: 1,
        insuranceAmountLevelId: undefined,
        effectiveDate: effectiveDateInput || effectiveDate,
        insuranceIdentityId: undefined,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  };

  return (
    <div data-test-id={'retirement'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>勞退</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-retirement'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`retirement.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <Show isShow>
          <FormItem
            label="生效日期"
            itemName="laborPension_effectiveDate"
            required
            width="160px"
            data-test-id={'effective-date'}
          >
            <Controller
              render={({onChange, value}) => (
                <Fragment>
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(onBoardingDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || isUnable}
                    onChange={(date: Date) => {
                      const dateString = formatYYYYMMDD(date);
                      onChange(dateString);
                      setValue(`retirement.insuranceAmountLevelId`, null);
                      updateInsuranceAmount({effectiveDateInput: dateString});
                      updateLocale();
                    }}
                    onBlur={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    onCalendarClose={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    error={isError(`retirement.effectiveDate`)}
                  />
                  {renderErrorMessage(`retirement.effectiveDate`)}
                </Fragment>
              )}
              name={`retirement.effectiveDate`}
              control={control}
              rules={{
                required: !isUnable && '生效日期為必填',
                validate: {
                  isAfterOnBoardingDate: v => {
                    let onBoardingDateObj = transferDateObj(onBoardingDate);
                    let effectiveDateObj = transferDateObj(v);
                    if (!isUnable && onBoardingDateObj && effectiveDateObj) {
                      return (
                        DateTime.fromJSDate(onBoardingDateObj) <=
                          DateTime.fromJSDate(effectiveDateObj) ||
                        `不可早於到職日${onBoardingDate}`
                      );
                    }
                    return true;
                  },
                },
              }}
              defaultValue={defaultValues.effectiveDate}
            />
          </FormItem>
          <FormItem
            label="類別"
            itemName="laborPension_category"
            required
            width="80px"
            data-test-id={'category'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={category.find(o => o.value === value)}
                  options={category}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`retirement.category`}
              control={control}
              defaultValue={defaultValues.category}
            />
          </FormItem>
          <FormItem
            label="籍別"
            itemName="laborPension_nationality"
            required
            width="120px"
            hidden={!isNewCategory}
            data-test-id={'nationality'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={nationality.find(o => o.value === value)}
                  options={nationality}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`retirement.nationality`}
              control={control}
              defaultValue={defaultValues.nationality}
            />
          </FormItem>
          <FormItem
            label="投保單位"
            itemName="laborPension_insuranceApplicantUnitId"
            required
            width="200px"
            hidden={!isNewCategory}
            data-test-id={'insurance-applicant-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceUnits.find(
                      (o: any) => o.insuranceApplicantUnitId === value,
                    ) || null
                  }
                  options={options.insuranceUnits}
                  onChange={(o: any) => {
                    onChange(o.insuranceApplicantUnitId);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) =>
                    `${o.insuranceNo} ${o.insuranceApplicantUnitName}`
                  }
                  getOptionValue={(o: any) => o.insuranceApplicantUnitId}
                  error={
                    isNewCategory &&
                    isError(`retirement.insuranceApplicantUnitId`)
                  }
                />
              )}
              name={`retirement.insuranceApplicantUnitId`}
              control={control}
              rules={{required: isNewCategory && !isUnable && '投保單位為必填'}}
              defaultValue={defaultValues.insuranceApplicantUnitId}
            />
          </FormItem>
          <FormItem
            label="投保金額"
            itemName="laborPension_insuranceAmountLevelId"
            required
            width="135px"
            hidden={!isNewCategory}
            data-test-id={'insurance-amount-level-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  isLoading={isInsuranceAmountFetching}
                  loadingMessage={() => '載入中...'}
                  value={
                    insuranceAmount.find((o: any) => o.value === value) || ''
                  }
                  options={insuranceAmount}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  onMenuOpen={() => {
                    if (insuranceAmount.length === 0) {
                      updateInsuranceAmount({});
                    }
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={
                    isNewCategory &&
                    isError(`retirement.insuranceAmountLevelId`)
                  }
                />
              )}
              name={`retirement.insuranceAmountLevelId`}
              control={control}
              rules={{required: isNewCategory && !isUnable && '投保金額為必填'}}
              defaultValue={defaultValues.insuranceAmountLevelId}
            />
          </FormItem>
          <FormItem
            label="雇主提繳率"
            itemName="laborPension_employerBurdenRate"
            required
            width="100px"
            hidden={!isNewCategory}
            data-test-id={'employer-burden-rate'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (
                      !value ||
                      /^(\d+(\.\d{0,1})?|\.?\d{1,1})$/.test(value)
                    ) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  error={
                    isNewCategory && isError(`retirement.employerBurdenRate`)
                  }
                />
              )}
              name={`retirement.employerBurdenRate`}
              control={control}
              rules={{
                required: isNewCategory && !isUnable && '雇主提繳率為必填',
                min: {
                  value: 0,
                  message: '雇主提繳率不得低於0%',
                },
                max: {
                  value: 100,
                  message: '雇主提繳率不得高於100%',
                },
              }}
              defaultValue={defaultValues.employerBurdenRate}
            />
            {renderErrorMessage(`retirement.employerBurdenRate`)}
          </FormItem>
          <FormItem
            label="個人提繳率"
            itemName="laborPension_personalBurdenRate"
            required
            width="100px"
            hidden={!isNewCategory}
            data-test-id={'personal-burden-rate'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (
                      !value ||
                      /^(\d+(\.\d{0,1})?|\.?\d{1,1})$/.test(value)
                    ) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  error={
                    isNewCategory && isError(`retirement.personalBurdenRate`)
                  }
                />
              )}
              name={`retirement.personalBurdenRate`}
              control={control}
              rules={{
                required: isNewCategory && !isUnable && '個人提繳率為必填',
                min: {
                  value: 0,
                  message: '個人提繳率不得低於0%',
                },
                max: {
                  value: 6,
                  message: '個人提繳率不得高於6%',
                },
              }}
              defaultValue={defaultValues.personalBurdenRate}
            />
            {renderErrorMessage(`retirement.personalBurdenRate`)}
          </FormItem>
        </Show>
      </Block>
    </div>
  );
};

export default LaborPensionData;
